import React from 'react'
import { OmDropdownMenu, type OmMenuItem } from '@owma/kit'
import i18n from 'i18n'
import styles from './EventEditor.module.scss'
import reminderIcon from 'assets/images/calendar/reminder-icon.svg'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import closeIcon from 'assets/images/toolbar-close.svg'

export { REMINDER_OPTIONS, ALLDAY_REMINDER_OPTIONS } from './reminderOptions'
import { REMINDER_OPTIONS, ALLDAY_REMINDER_OPTIONS } from './reminderOptions'

interface ReminderOption {
  key: number
  label: string
}

const formatReminderTime = (seconds: number): string => {
  if (seconds === 0) return i18n.t('calendar.alarm.atTimeOfEvent')

  const absSeconds = Math.abs(seconds)
  const days = Math.floor(absSeconds / 86400)
  const hours = Math.floor((absSeconds % 86400) / 3600)
  const mins = Math.floor((absSeconds % 3600) / 60)

  const parts: string[] = []
  if (days > 0)
    parts.push(`${days} ${days === 1 ? i18n.t('calendar.time.day') : i18n.t('calendar.time.days')}`)
  if (hours > 0)
    parts.push(
      `${hours} ${hours === 1 ? i18n.t('calendar.time.hour') : i18n.t('calendar.time.hours')}`,
    )
  if (mins > 0)
    parts.push(
      `${mins} ${mins === 1 ? i18n.t('calendar.time.minute') : i18n.t('calendar.time.minutes')}`,
    )

  return `${parts.join(' ')}${i18n.t('calendar.alarm.before')}`
}

interface EditEventData {
  alarms?: number[]
}

interface ReminderSelectorProps {
  editEventData: EditEventData
  onReminderChange: (index: number, seconds: number) => void
  onAddReminder: () => void
  onRemoveReminder: (index: number) => void
  isAllDay: boolean
}

const ReminderSelector: React.FC<ReminderSelectorProps> = ({
  editEventData,
  onReminderChange,
  onAddReminder,
  onRemoveReminder,
  isAllDay,
}) => {
  const getRemainReminderItems = (): ReminderOption[] => {
    const orgOptions = isAllDay ? ALLDAY_REMINDER_OPTIONS() : REMINDER_OPTIONS()
    const options = orgOptions.filter((option) => !editEventData.alarms?.includes(option.key))
    return options
  }

  const getReminderItems = (index: number): OmMenuItem[] => {
    return getRemainReminderItems().map((option) => ({
      key: String(option.key),
      label: option.label,
      onClick: () => onReminderChange(index, option.key),
    }))
  }

  const getReminderLabel = (seconds: number): string => {
    const options = isAllDay ? ALLDAY_REMINDER_OPTIONS() : REMINDER_OPTIONS()
    const predefinedOption = options.find((option) => option.key === seconds)
    return predefinedOption ? predefinedOption.label : formatReminderTime(seconds)
  }

  return (
    <div className={styles.reminderWrapper}>
      <img src={reminderIcon} alt="Reminder" className={styles.titleIcon} />
      <div className={styles.reminderContent}>
        {editEventData.alarms?.map((reminder, index) => (
          <div key={index} className={styles.reminderRow}>
            <OmDropdownMenu
              menu={{ items: getReminderItems(index) }}
              trigger={['click']}
              overlayStyle={{ width: 240 }}
              overlayClassName={'contextMenu'}
              placement="bottomLeft"
              disabled={getRemainReminderItems().length <= 0}
            >
              <button className={styles.reminderButton}>
                <span>{getReminderLabel(reminder)}</span>
                <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
              </button>
            </OmDropdownMenu>
            <button className={styles.removeButton} onClick={() => onRemoveReminder(index)}>
              <img src={closeIcon} alt="Remove" />
            </button>
          </div>
        ))}
        {getRemainReminderItems().length > 0 && (
          <button className={styles.addReminderButton} onClick={onAddReminder}>
            {i18n.t('calendar.edit.addReminder')}
          </button>
        )}
      </div>
    </div>
  )
}

export default ReminderSelector
